import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { environment } from 'src/environments/environment';
import { AppError, toAppError } from './app-error';
import { AuthService } from 'src/app/auth/services/auth.service';
import { DevErrorOverlayService } from '../dev-error/dev-error-overlay.service';
import { errorName } from 'src/assets/common/enums/error-name.g';
import { NotificationService } from '../notification/notification.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { logging } from '../log-manager/log-manager';

/**
 * Centrale fout-funnel. Elke fout — uit de HttpClient-interceptor, de openapi-fetch-middleware
 * of een ongevangen throw (globale ErrorHandler) — loopt hier langs: normaliseren → loggen →
 * (optioneel) vertaalde notificatie → (dev) debug-overlay.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService implements ErrorHandler {
    private readonly _notificationService = injectRequired(NotificationService);
    private readonly _devErrorOverlay     = injectRequired(DevErrorOverlayService);
    private readonly _transloco           = injectRequired(TranslocoService);
    private readonly __authService        = injectRequired(AuthService);
    private readonly __router             = injectRequired(Router);
    private readonly __logger             = logging.getLogger('error');

    // Voorkomt dat een uitgeloggd/geblokkeerd account meerdere keren wordt afgehandeld: een
    // geblokkeerd account levert al bij de eerste page-load meteen een handvol parallelle 401's op
    // (Account/details, AdminPanel/details, UserState, DomainUser/Invitation list-from-user, ...),
    // die anders stuk voor stuk hun eigen logout+navigatie+toast zouden triggeren. Reset zodra de
    // navigatie naar het loginscherm daadwerkelijk is voltooid, zodat een latere, ECHTE herhaling
    // (bv. opnieuw met hetzelfde geblokkeerde account inloggen) gewoon weer wordt getoond.
    private __accountBlockedHandled = false;

    /**
     * Globale ErrorHandler-ingang. HTTP-fouten zijn al door de transport-adapters
     * (interceptor / api.service) gerapporteerd; alleen overige ongevangen fouten verwerken.
     */
    handleError(error: unknown): void {
        if (error instanceof HttpErrorResponse) return;
        this.report(error);
    }

    /**
     * Eén verwerkingspunt voor álle fouten. `suppressNotification` onderdrukt ALLEEN de
     * vertaalde gebruikersnotificatie (voor achtergrond-probes met een verwachte foutstatus,
     * bv. een 403 op een is-admin-check) — logging en de dev-debug-overlay voor onverwachte 5xx
     * blijven altijd actief. Eerder blokkeerde de aanroeper (API_SUPPRESS_ERROR_HEADER in
     * api.service.ts) deze hele methode, waardoor een échte bug op zo'n aanroep (bv. een 500
     * DirectoryNotFoundException) ook nooit gelogd werd of de dev-overlay toonde — onzichtbaar
     * tot een gebruiker het symptoom elders opmerkte.
     */
    report(input: unknown, options?: { suppressNotification?: boolean }): void {
        const _err: AppError = toAppError(input);

        this.__logger.error(
            `[${_err.name}] status=${_err.status ?? '-'}${_err.serverMessage ? ' ' + _err.serverMessage : ''}`
        );

        // Geblokkeerd (soft-deleted) account: elke volgende request met dit token faalt op dezelfde
        // manier, dus blijven hangen heeft geen zin — meteen lokaal uitloggen en terug naar login,
        // net als AuthService.__authChanged_elsewhere bij een cross-tab account-wissel. Bij de EERSTE
        // van mogelijk meerdere gelijktijdige 401's op dit account (zie __accountBlockedHandled
        // hierboven); latere gelijktijdige aanroepen worden hier stil verworpen, ook voor de
        // notificatie verderop — anders ziet de gebruiker dezelfde melding een paar keer na elkaar.
        if (_err.name === errorName.accountBlocked) {
            if (this.__accountBlockedHandled) return;
            this.__accountBlockedHandled = true;

            this.__authService.revokeToken();
            void this.__router.navigateByUrl(this.__authService.LOGIN_PATH)
                .then(() => { this.__accountBlockedHandled = false; });
        }

        // Development: debug-overlay alleen voor ONVERWACHTE fouten (5xx / transport / ongevangen).
        // Verwachte 4xx (autorisatie, validatie, conflict) zijn normale flow → hooguit een notificatie.
        const _unexpected = _err.status == null || _err.status === 0 || _err.status >= 500;
        if (!environment.production && _err.debug && _unexpected) {
            this._devErrorOverlay.show(_err.debug);
        }

        // Gebruikersnotificatie: vertaalde titel + tekst op basis van de errorName + params.
        if (_err.showInUI && !options?.suppressNotification) {
            this._notificationService.pushNotification({
                title:    this.__errorTitle(_err.name),
                text:     this._transloco.translate(`errors.${_err.name}.text`, _err.params),
                duration: _err.duration,
                offline:  _err.offline,
                // Geen verbinding = let op (tijdelijk); al het andere dat hier binnenkomt is een fout.
                severity: _err.offline ? 'warning' : 'error',
            });
        }
    }

    /** Eigen fout-titel (errors.<name>.title); valt terug op de generieke titel als die ontbreekt. */
    private __errorTitle(name: string): string {
        const _key   = `errors.${name}.title`;
        const _title = this._transloco.translate(_key);
        return _title && _title !== _key ? _title : this._transloco.translate('notification.errorTitle');
    }
}
